"use client";

import Image from "next/image";
import { TerraLink } from "@/components/terra/component/TerraLink/TerraLink";
import { CommonDescription } from "@/components/terra/component/CommonDescription/CommonDescription";
import type { FilterableCardProps } from "@/components/terra/layouts/LayoutFilterableCards/FilterableCard";
import { useCallback, useRef, useState, type MouseEvent } from "react";
import "./MarketplaceCard.css";

/** Same entry shape as {@link FilterableCardProps}, plus optional marketplace-only chrome. */
export type MarketplaceCardProps = Omit<FilterableCardProps, "imageDisplayMode"> & {
  /**
   * How the card image is shown: `"logo"` (contained in dashed area, default) or `"teaser"` (full-width cover).
   * @default "logo"
   */
  imageDisplayMode?: "logo" | "teaser";
  /** Optional 32px top banner: white text on Trimble blue (#0063A3). */
  textLabel?: string | null;
  /** Developer / company name shown below the card title. */
  developer?: string | null;
  /** Developer URL shown below the card title. */
  developerUrl?: string | null;
  /** Developer title shown below the card title. */
  developerTitle?: string | null;
  /** Category tags rendered above the CTA. */
  categoryTags?: string[];
  /**
   * "Certified by Trimble" flag. When `true`, the card renders a blue
   * top banner ("CERTIFIED APP") in the same slot used by
   * {@link MarketplaceCardProps.textLabel} — matching the top-ribbon
   * treatment in the marketplace card design system.
   *
   * Sourced from the PDP's `trimbleCertified` Boolean field in
   * Contentful, which in turn is populated by the AppDirect sync from
   * `product.productRibbon.text` (any non-empty ribbon text is treated
   * as certified). See `transformPDP` in
   * `trimble-mxp-api/src/lib/appdirect/transform.ts` and the
   * "Certified App" ribbon in the AppDirect merchandising settings
   * (Screenshot: AppDirect > Product > Merchandising > Ribbon).
   *
   * When an explicit {@link MarketplaceCardProps.textLabel} is also
   * passed, that wins — treat this flag as the default label source.
   * Kept as a first-class prop (rather than always plumbing the raw
   * ribbon text) so a schema-free frontend fix is possible without
   * waiting on a Contentful migration for a ribbon-text field.
   */
  trimbleCertified?: boolean;
  /**
   * Original AppDirect tag-group structure (e.g. Categories, Created By,
   * Featured, Geographies). Used by listing pages to drive a filter sidebar
   * — the card itself does not render this.
   */
  tagGroups?: Record<string, string[]>;
  titleId?: string;
  className?: string;
  isLCP?: boolean;
  description?: string | null;
  /**
   * Ref callback applied to the card's outermost element. Listing
   * pages use this to register the card with a shared
   * `IntersectionObserver` for `view_item_list` impression tracking
   * (see `useProductListingImpressions`). Stays optional so unrelated
   * consumers (Storybook, mock data, the marketplace PDP, etc.) keep
   * working without any analytics plumbing.
   */
  impressionRef?: (el: HTMLElement | null) => void;
  /**
   * Fired immediately before the card's `<TerraLink>` navigates. The
   * argument indicates whether the click originated on the CTA pill
   * (`"cta"`) or anywhere else inside the card (`"card"`). Listing
   * pages use this to push a `select_item` event without coupling the
   * card to any analytics module directly.
   */
  onTrackedClick?: (target: "card" | "cta") => void;
};

/**
 * Marketplace-styled card using the same content fields as {@link FilterableCard}.
 */
export function MarketplaceCard({
  image,
  imageDisplayMode = "logo",
  header,
  description,
  button,
  truncate,
  truncateLength,
  textLabel,
  developer,
  developerTitle = "Developer",
  developerUrl,
  categoryTags,
  trimbleCertified = false,
  tagGroups,
  className,
  titleId,
  isLCP = false,
  impressionRef,
  onTrackedClick
}: MarketplaceCardProps) {
  // Pills should only show the product category/type — not geolocation,
  // languages, "created by", or compatible-with values, even though all
  // of those live in the flat `categoryTags` list. The structured
  // `tagGroups` slots are populated by the classifier in
  // `productListingData.ts`.
  //
  // Falls back to the flat `categoryTags` only as a defensive backstop —
  // every product card built through `formatProductDetailPageToCard`
  // synthesizes `tagGroups`, so this branch is essentially never hit.
  //
  // Certified products use the blue top ribbon ("CERTIFIED APP") as
  // the only certified callout. Matching pills ("Certified by Trimble",
  // "Trimble Certified App") are filtered out of the metadata row so
  // the status is not shown twice. Gated on the same `trimbleCertified`
  // flag (populated by the AppDirect sync from
  // `product.productRibbon.text`, see `transformPDP` in
  // `trimble-mxp-api`).
  const baseTags = tagGroups ? [...(tagGroups.Categories ?? []), ...(tagGroups.Certifications ?? [])] : categoryTags ?? [];
  const pillTags = trimbleCertified ? baseTags.filter(t => !isCertifiedCalloutTag(t)) : baseTags;

  // Top ribbon banner: explicit `textLabel` prop wins when set,
  // otherwise derive from the certified flag using AppDirect's own
  // ribbon copy.
  const CERTIFIED_RIBBON_TEXT = "Certified App";
  const resolvedTextLabel = textLabel != null && textLabel !== "" ? textLabel : trimbleCertified ? CERTIFIED_RIBBON_TEXT : null;
  const [isPointerOnCta, setPointerOnCta] = useState(false);
  const [isFocusOnCta, setFocusOnCta] = useState(false);
  const isCtaHighlighted = isPointerOnCta || isFocusOnCta;
  if (!header) return null;
  const destination = button?.destination;
  const hasDestination = Boolean(destination);
  const showCta = Boolean(button?.label && destination);
  const headingId = titleId ?? `marketplace-card-title-${hashStable(header)}`;

  // Single click handler for both the card body and the CTA pill: they
  // share the outer `<TerraLink>` anchor, so we always run here once
  // per user click. We classify "where" the click landed (the CTA span
  // vs. anywhere else) by walking up the DOM from `e.target` and
  // surface that to the analytics caller via {@link onTrackedClick}.
  // We do NOT preventDefault when a destination exists — the link must
  // still navigate. The dataLayer push performed by the consumer is
  // synchronous, so it queues before navigation begins.
  const handleClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    if (!hasDestination) {
      e.preventDefault();
      return;
    }
    if (!onTrackedClick) return;
    const target = e.target as HTMLElement | null;
    const clickedCta = target?.closest(".t2-button") !== null;
    onTrackedClick(clickedCta ? "cta" : "card");
  }, [hasDestination, onTrackedClick]);

  // The IntersectionObserver wired up by `useProductListingImpressions`
  // needs a DOM node to observe. `<TerraLink>` doesn't forward refs, so
  // we attach the impression ref to the inner `<article>` element
  // instead — its bounding box is identical to the anchor's (article
  // fills the link via `w-full h-full` flex), so the observed
  // visibility is equivalent. We also keep a local ref so subsequent
  // renders don't repeatedly trigger the consumer's
  // observe/unobserve cycle.
  const articleElRef = useRef<HTMLElement | null>(null);
  const setArticleRef = useCallback((el: HTMLElement | null) => {
    articleElRef.current = el;
    impressionRef?.(el);
  }, [impressionRef]);
  const renderCardImage = () => {
    if (imageDisplayMode === "teaser") {
      if (!image?.url) {
        return <div className="w-full min-h-[200px] shrink-0 bg-gray-m1" aria-hidden="true" />;
      }
      return <div className="w-full min-h-[200px] shrink-0 bg-cover bg-center text-transparent select-none" style={{
        backgroundImage: `url(${image.url})`
      }} role="img" aria-label={image.alt || ""} />;
    }

    // logo mode — always reserve the image area so every card has a consistent
    // top section, even when the product has no listing image yet.
    //
    // Uses `fill` (rather than numeric `width` / `height` props) because
    // the parent container has a fixed 75px height and the source
    // images come in varying aspect ratios. With `fill` + `object-scale-
    // down`, Next.js positions the image `absolute inset-0` inside the
    // relatively-positioned parent and lets `object-fit` handle the
    // aspect ratio, so we don't have to override CSS dimensions on the
    // <Image> itself.

    return <div className="px-8 py-6 pb-0 pt-16">
        <div className="relative w-full h-[75px] shrink-0 overflow-hidden bg-white">
          {image?.url ? <Image src={image.url} alt={image.alt ?? ""} fill className="object-scale-down object-left" sizes="332px" loading={isLCP ? "eager" : "lazy"} /> : <div aria-hidden="true" className="h-full w-full" />}
        </div>
      </div>;
  };
  return <TerraLink disableStyles destination={destination ?? "#"} ariaLabel={button?.label ? `${button.label}: ${header}` : header} tabIndex={hasDestination ? undefined : -1} onClickHandler={handleClick as () => void} onMouseEnter={() => setPointerOnCta(true)} onMouseLeave={() => setPointerOnCta(false)} onFocus={() => setFocusOnCta(true)} onBlur={() => setFocusOnCta(false)} className={`t2-marketplace-card group block h-full w-full text-gray-m9 max-w-[400px] rounded-[12px] no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-m9 focus-visible:ring-offset-2 focus-visible:ring-offset-white${hasDestination ? "" : " cursor-default"}`}>
      <article ref={setArticleRef} className={`t2-c relative isolate bg-white box-border w-full h-full flex flex-col rounded-[12px] overflow-hidden t2-color-setting--light ${className ? ` ${className}` : ""}`} aria-labelledby={headingId}>
        {resolvedTextLabel && <span className="absolute top-0 left-0 right-0 m-0 shrink-0 flex h-[32px] items-center px-8 text-med font-bold leading-none text-white bg-blue-trmb truncate uppercase justify-center z-10" title={resolvedTextLabel}>
            {resolvedTextLabel}
          </span>}
        <span aria-hidden="true" className={`pointer-events-none absolute inset-0 rounded-[12px] border-2 transition-colors duration-150 ${isCtaHighlighted ? "border-blue-trmb" : "border-transparent"}`} />

        {renderCardImage()}

        <div className={`flex flex-col flex-grow px-8 py-6 max-sm:pt-4 min-h-0`}>

          <h3 id={headingId} className="text-gray-m9 text-[26px] font-light sm:leading-[1.2] break-words m-0 mb-2">
            {header}
          </h3>

          {developer ? developerUrl ? <p className="text-sm font-normal leading-snug m-0 mb-2">
              {developerTitle}: <TerraLink className="font-semibold" linkType="text" destination={developerUrl}>{developer}</TerraLink>
            </p> : <p className="text-sm font-normal leading-snug m-0 mb-2">
              {developerTitle}: <span className="font-semibold">{developer}</span>
            </p> : null}

          {description ? <CommonDescription description={description} truncate={truncate} truncateLength={truncateLength} className={`text-base font-normal leading-relaxed line-clamp-3 m-0 [&_p]:m-0 ${developer ? "mt-0" : "mt-2"}`} /> :
        // Reserve the description slot even when the product has no
        // description, so cards in the same row keep matching visual rhythm.
        <div aria-hidden="true" className="min-h-[3.5rem]" />}
        </div>

        {pillTags && pillTags.length > 0 ? <div className="flex flex-wrap gap-2 px-8 pb-5 mt-auto">
            {pillTags.map((tag, index) => <span key={`${tag}-${index}`} className="inline-block bg-gray-m1 px-3 py-1 text-sm font-bold leading-tight">
                {tag}
              </span>)}
          </div> : null}

        {showCta && button ? <div className="px-8 pb-12 pt-5 mt-auto shrink-0 text-center">
            <span className="t2-button t2-button--primary t2-button--medium rounded-full inline-block font-bold text-center uppercase [text-decoration:none]! bg-yellow-m10 border-2 border-yellow-m10 text-gray-m9 transition-[color,background-color,border-color,box-shadow] duration-150 ease-in-out group-hover:bg-yellow-m10-hover group-hover:border-yellow-m10-hover text-lg tracking-[1.44px] leading-7 py-4 px-9 max-sm:text-base max-sm:py-1.5 max-sm:px-[34px]">
              {button.label}
            </span>
          </div> : null}
      </article>
    </TerraLink>;
}

/** Certified-status copy that belongs on the top ribbon, not in the pill row. */
function isCertifiedCalloutTag(tag: string): boolean {
  const normalized = tag.trim().toLowerCase();
  return normalized === "certified by trimble" || normalized === "trimble certified app" || normalized === "certified app";
}

/** Tiny deterministic id suffix so multiple cards on a page get unique heading ids. */
function hashStable(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) {
    h = (h << 5) - h + input.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
}